import { decodeJwt } from 'jose';
import { evaluateHeaders } from './utils.js';
let logger = () => { };
const DEFAULT_ENDPOINT = 'https://api.share.vendia.com/graphql';
const DEFAULT_OAUTH_URL = 'https://auth.share.vendia.com/token';
const SDK_HEADER = '@vendia/control-plane-api';
export class ControlPlaneClient {
    options;
    fetcher;
    additionalHeaders = {};
    constructor(opts = {}) {
        this.options = opts;
        if (opts.debug) {
            logger = console.log;
        }
        if (opts.headers) {
            this.additionalHeaders = opts.headers;
        }
        const endpoint = opts.endpoint || DEFAULT_ENDPOINT;
        this.fetcher = opts.apiCredentials
            ? createClientCredentialsFetcher(endpoint, opts.apiCredentials, opts.oauthUrl)
            : createAccessTokenFetcher(endpoint, opts.getCredentials);
    }
    async graphqlFetch(data) {
        logger('ControlPlaneClient.graphqlFetch request:', data);
        try {
            // Inject asRole if the caller provides getAsRole and the variable isn't already set
            let fetchData = data;
            if (typeof this.options.getAsRole === 'function' && data.variables?.asRole == null) {
                const asRole = await this.options.getAsRole();
                logger('ControlPlaneClient.graphqlFetch asRole:', asRole);
                fetchData = { ...data, variables: { ...data.variables, asRole } };
            }
            const evaluatedHeaders = await evaluateHeaders(this.additionalHeaders);
            logger('ControlPlaneClient.graphqlFetch headers:', evaluatedHeaders);
            const res = await this.fetcher(JSON.stringify(fetchData), {
                'Content-Type': 'application/json',
                'x-vendia-sdk': SDK_HEADER,
                ...evaluatedHeaders,
            });
            logger('ControlPlaneClient.graphqlFetch response:', res);
            const graphqlData = res?.data;
            const graphqlErrors = res?.errors;
            // Flatten response so errors property is same level as graphql response queries
            // This matches the legacy SDK's response format that all consumers expect
            const flattenedRes = graphqlData ? { ...graphqlData, errors: graphqlErrors } : { errors: graphqlErrors };
            return flattenedRes;
        }
        catch (error) {
            logger('ControlPlaneClient.graphqlFetch error', error);
            if (this.options.onError && typeof this.options.onError === 'function') {
                await this.options.onError(error);
            }
            throw error;
        }
    }
}
const createAccessTokenFetcher = (endpoint, getCredentials) => {
    return async (body, headers) => {
        const accessToken = getCredentials ? await getCredentials() : undefined;
        if (accessToken) {
            headers.Authorization = `Bearer ${accessToken}`;
        }
        const response = await fetch(endpoint, {
            method: 'POST',
            headers,
            body,
        });
        if (!response.ok) {
            const error = new Error(`Network response was not ok: ${response.statusText}`);
            error.status = response.status;
            error.statusText = response.statusText;
            throw error;
        }
        return response.json();
    };
};
const createClientCredentialsFetcher = (endpoint, apiCredentials, oauthUrl) => {
    let accessToken = '';
    const tokenUrl = oauthUrl || DEFAULT_OAUTH_URL;
    return async (body, headers) => {
        accessToken = await getAliveAccessToken(apiCredentials, accessToken, tokenUrl);
        headers.Authorization = `Bearer ${accessToken}`;
        const response = await fetch(endpoint, {
            method: 'POST',
            headers,
            body,
        });
        if (!response.ok) {
            const error = new Error(`Network response was not ok: ${response.statusText}`);
            error.status = response.status;
            error.statusText = response.statusText;
            throw error;
        }
        return response.json();
    };
};
const getAliveAccessToken = async (apiCredentials, currentToken, oauthUrl) => {
    if (isTokenExpired(currentToken)) {
        const res = await refreshAccessToken(apiCredentials, oauthUrl);
        return res.access_token;
    }
    return currentToken;
};
const isTokenExpired = (token) => {
    try {
        if (!token?.length) {
            return true;
        }
        const decodedJwt = decodeJwt(token);
        if (!decodedJwt?.exp) {
            return true;
        }
        // Return true if the token is expired or about to expire (in ~5s)
        const tokenExpiration = decodedJwt.exp * 1000;
        return tokenExpiration < Date.now() + 5000;
    }
    catch {
        return true;
    }
};
const refreshAccessToken = async (apiCredentials, oauthUrl) => {
    const urlEncodedBody = new URLSearchParams({
        grant_type: 'client_credentials',
        client_id: apiCredentials.clientId,
        client_secret: apiCredentials.clientSecret,
    }).toString();
    const response = await fetch(oauthUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncodedBody,
    });
    if (response.status >= 400) {
        throw new Error('Unable to exchange client ID and Secret for an Access Token.');
    }
    return response.json();
};
//# sourceMappingURL=client.js.map